import React, { useState, useContext, useEffect, createRef, useRef } from 'react';
import QuoteBuilderList from 'components/quote_builder/QuoteBuilderList.jsx';
import { PostRequest, GetRequest, Tooltip } from 'components/common';
import { applySaveValueInto, seedSaveValueIntoFields } from 'components/common/saveValueInto';
import { attributeProperty } from 'components/common/attributeProperty';
import { HiddenFormField, HeadingFormField, SelectFormField, LayoutDimensionSelectFormField, GlueingMaxMaterialLengthSelectFormField, GlueingPointCountSelectFormField, LengthFormField, StringFormField, NumberFormField, DelayedNumberFormField, DelayedNumberWithUnitFormField, DelayedNumberWithStaticUnitsFormField, CurrencyFormField, CurrencyExchange, PriceFormField, UserUnitPrice, UserUnitPriceAttributeField, DescriptionLong, LayoutPartGroupsWindows, LayoutPartGroupsWindowsHidden, TextAreaFormField, FileFormField, CheckboxesFormField, BooleanFormField } from 'components/quote_builder/form_fields';
import DrawingFiltering from 'components/system_commands/form_fields/DrawingFiltering.jsx';
import { Form, Button } from 'semantic-ui-react';
import { useForm  } from "react-hook-form";
import hash from "object-hash";

const QuoteBuilderForm = ({ config, disabled, showingState, linkToSettings, beforeSave, afterSave, onSaveError, afterCancel, editStateId, editedKlass, options, readOnlyValues, section_uuid, item, extra_title, navSection, colIndex, rowIndex, getExtraItems, navSectionColumnType, context, hideDeleteFromList, saveLabel, showNewForm }) => {
  const default_fetch_path = '/quote-builder/user_unit_prices/:id';
  const default_fetch_system_path = '/quote-builder/user_unit_prices_system/:id';
  const default_update_path = '/quote-builder/user_unit_prices/update';
  const default_create_path = '/quote-builder/user_unit_prices/create/:key'

  const USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY = 'quote_builder.user_unit_price_changed_with_item';

  const STATE_LIST = 'list';
  const STATE_NEW  = 'new';
  const STATE_EDIT = 'edit';
  const STATE_COPY_FROM_SYSTEM  = 'copy_from_system';
  const VALUE_NOT_AVAILABLE = 'not-available'
  const VALUE_NEW = "new-attribute";
  const keyFor = (config) => hash(config);
  const v = (key) => { return values[key] }
  const va = (key) => { return values['user_unit_price_attribute.' + key] }

  // Registry of loaded UserUnitPriceAttributes (machines, materials, ...) keyed by id. Each picker
  // reports its list up via onAttributesLoaded as it loads, so settings.yml expressions can read a
  // SELECTED attribute's properties (not just its id, which is all va() gives). This is a useRef, so
  // populating it from onAttributesLoaded does NOT trigger a re-render: fine for the current vap()
  // user (a hidden field evaluated fresh at save), but a *visible* field keyed on vap() would not
  // re-render when the picker list finishes loading until the next unrelated render.
  const attributesById = useRef({});
  const registerAttributeProperties = (loadedAttributes) => {
    (loadedAttributes || []).forEach((attribute) => {
      if (attribute && attribute.id != undefined) { attributesById.current[attribute.id] = attribute; }
    });
  };
  // makeVap(bag): build vap('printing_machine', 'printing_type') bound to a value bag, so a
  // settings.yml visible_if resolves the SELECTED attribute's property from the same bag it is
  // evaluated against — live values on save, the freshly fetched record on seed. See
  // components/common/attributeProperty.js.
  const makeVap = (bag) => (attributeKey, propertyName) => attributeProperty(bag, attributesById.current, attributeKey, propertyName);
  // vap resolves against the live `values`, read LAZILY at call time (values is declared further below):
  // binding eagerly here would hit a temporal-dead-zone ReferenceError under strict const semantics
  // (jest) and capture an undefined `values` where const is transpiled to var (the browser build).
  const vap = (attributeKey, propertyName) => makeVap(values)(attributeKey, propertyName);

  // Check if we're on the settings page to enable URL synchronization
  const isSettingsPage = () => {
    return window.location.pathname.includes('/quote-builder/settings');
  }

  // Update URL when editing state changes (only on settings page)
  const updateUrlForEditState = (state, itemId, configKey) => {
    if (!isSettingsPage()) return;

    if (state === STATE_EDIT && itemId) {
      window.history.replaceState({}, '', `/quote-builder/settings/edit/${itemId}`);
    } else if (state === STATE_NEW && configKey) {
      window.history.replaceState({}, '', `/quote-builder/settings/new/${configKey}`);
    } else {
      window.history.replaceState({}, '', '/quote-builder/settings');
    }
  }

  const is_any_new_value = (list) => { return list.indexOf(VALUE_NEW) > -1 }
  const is_not_new_value = (list) => { return list.indexOf(VALUE_NEW) == -1 }
  const is_not_new_value_va = (list) => { return is_not_new_value(list.map((key) => va(key))) }

  const [submitInProgress, setSubmitInProgress] = useState(false);
  const has_file = useRef('');

  const zeroPriceValueFor = (attributeName) => {
    const attributeConfig = config.attributes.find((candidate) => candidate.name === attributeName);
    const precision = attributeConfig != undefined && attributeConfig.precision != undefined ? attributeConfig.precision : 2;
    return (0).toFixed(precision);
  };

  // A multi-select (checkboxes) attribute added after a record was created is absent from that
  // record and arrives null from the server. Seed its default_value (typically all options) so it
  // renders like a new record rather than empty; an explicit saved selection arrives as an array
  // (even an empty one) and is used as-is, so unchecking and saving still sticks.
  const multiSelectDefaultFor = (attributeName) => {
    const attributeConfig = config.attributes.find((candidate) => candidate.name === attributeName);
    if (attributeConfig != undefined && attributeConfig.form_type === 'checkboxes' && attributeConfig.default_value != undefined) {
      return attributeConfig.default_value;
    }
    return undefined;
  };

  const defaultValueByFormType = (config, willFetchData) => {
    if(config.form_type == 'price' || config.form_type == 'price_with_precision_4') {
      if (willFetchData) return null;
      const precision = config.precision != undefined ? config.precision : 2;
      return (0).toFixed(precision);
    }

    return {
      'UserUnitPriceAttribute': '',
      'UserUnitPrice': 'not-available',
      'select': '',
      'checkboxes': [],
      'boolean': false,
      'hidden': '',
      'length': '0.00',
      'number': '0.00',
      'integer': '0',
      'currency': window.default_currency
    }[config.form_type]
  };

  const windowNavSection = () => {
    // Guard against undefined navSection prop
    if (!navSection) {
      console.warn('[QuoteBuilderForm] navSection prop is undefined');
      return {};
    }

    // Guard against undefined window.nav_sections
    if (!window.nav_sections || !Array.isArray(window.nav_sections)) {
      console.warn('[QuoteBuilderForm] window.nav_sections is not available, using navSection prop');
      return { ...navSection };
    }

    let result = undefined;

    // Try 1: Match by index/sort_order (current behavior)
    for(let i=0; i < window.nav_sections.length; i++) {
      if(window.nav_sections[i].sort_order === navSection.index) {
        result = window.nav_sections[i];
        break;
      }

      if(window.nav_sections[i].index === navSection.index) {
        result = window.nav_sections[i];
        break;
      }
    }

    // Try 2: If index doesn't match, try label (fallback)
    if(result === undefined && navSection.label !== undefined) {
      for(let i = 0; i < window.nav_sections.length; i++) {
        if(window.nav_sections[i].label === navSection.label) {
          result = window.nav_sections[i];
          console.warn(`[QuoteBuilderForm] navSection matched by label instead of index (${navSection.index}). This may indicate stale props.`);
          break;
        }
      }
    }

    // Try 3: If still not found, use the navSection prop itself with defaults
    if(result === undefined) {
      console.warn(
        `[QuoteBuilderForm] windowNavSection could not find match for navSection.index=${navSection.index}, ` +
        `navSection.label="${navSection.label}", user_quote_id=${window.user_quote_id}. ` +
        `Using provided navSection with default values.`
      );

      // Track this as a potential bug for monitoring
      trackEvent('quote_builder_nav_section_not_found', {
        navSection_label: navSection.label,
        navSection_index: navSection.index,
        user_quote_id: window.user_quote_id,
        window_nav_sections_count: window.nav_sections.length,
        window_nav_sections_labels: window.nav_sections.map(ns => ns.label).join(','),
        window_nav_sections_indexes: window.nav_sections.map(ns => ns.index || ns.sort_order).join(',')
      });

      // Return navSection prop with safe defaults
      result = { ...navSection };
    }

    return result;
  }

  const partType = () => {
    return navSection.part_type;
  }

  const d = (varName) => {
    if(config.dynamic_variables == undefined) { return; }

    for(let i = 0; i < config.dynamic_variables.length; i++ ){
      if(config.dynamic_variables[i].name == varName) {
        return eval(config.dynamic_variables[i].value)
      };
    }
  }

  const item_uuid_and_extra_item = () => {
    //needs to be same with /Users/emre/code/boxes/lib/quote_builder/models/user_quote_nav_column.rb
    return [item.uuid, item.extra_item_reason].join('_')
  }

  const userUnitPriceAttributes = useRef([]);

  const addToUsetUnitPriceAttributes = (name) => {
    if (userUnitPriceAttributes.current == undefined) { userUnitPriceAttributes.current = []; }
    userUnitPriceAttributes.current.push(name);
    userUnitPriceAttributes.current = userUnitPriceAttributes.current.filter(onlyUnique);
  }

  const item_and_name_exist_in_user_quote_section_properties = (name) => {
    return item != undefined && window.user_quote_section_properties != undefined && window.user_quote_section_properties[item_uuid_and_extra_item()] != undefined && window.user_quote_section_properties[item_uuid_and_extra_item()][section_uuid] != undefined && window.user_quote_section_properties[item_uuid_and_extra_item()][section_uuid][name] != undefined
  }

  const initialValues = () => {
    // Determine if we'll fetch data (edit mode with an ID to edit)
    const willFetchData = showingState === STATE_EDIT && editStateId > 0 && config.fetch_enabled;

    let values = {
      user_quote_id: window.user_quote_id,
      nav_section_column_type: navSectionColumnType,
      section_uuid: section_uuid,
      id: '',
      price_attributes: [],
      user_exchange_rates: {},
    };

    if(window.user_quote != undefined && window.user_quote.layout_part_group_windows.length > 0) {
      for(let i=0; i < window.user_quote.layout_part_group_windows.length; i++) {
       if(window.user_quote.layout_part_group_windows[i].value != undefined) {
         values[window.user_quote.layout_part_group_windows[i].attribute_name] = window.user_quote.layout_part_group_windows[i].value;
       } else {
         values[window.user_quote.layout_part_group_windows[i].attribute_name] = 'not-set';
       }
      }
    }

    if(navSection != undefined) {
      values.nav_section = navSection;
      values.row_index = rowIndex;
      values.item = item;
    }

    config.attributes.map( (config) => {
      if(config.form_type == 'price') {
        values[ 'price_attributes' ] = values[ 'price_attributes' ].concat([config.name]);
      }

      if (item_and_name_exist_in_user_quote_section_properties(config.name) ) {
        values[config.name] = window.user_quote_section_properties[item_uuid_and_extra_item()][section_uuid][config.name];
      } else {
        if (config.default_value != undefined) {
          values[config.name] = config.default_value;
        } else {
          if (config.default_value_eval != undefined) {
            values[config.name] = eval(config.default_value_eval);
          } else {
            if(config.default_value_in_values != undefined) {
              values[config.name] = values[config.default_value_in_values];
            } else {
              values[config.name] = defaultValueByFormType(config, willFetchData);
            }
          }
        }
      }
    });

    return values;
  }

  const initialShowingState = () => {
    if (showingState != undefined) return showingState;
    if (editStateId && config.list.enabled) return STATE_EDIT;
    return config.list.enabled ? STATE_LIST : STATE_NEW;
  };
  const initialEditId = () => {
    return editStateId || null;
  };

  const [componentShowingState, setComponentShowingState] = useState(initialShowingState());
  const [fetchInProgress, setFetchInProgress] = useState(false);
  const [childSubFormOpen, setChildSubFormOpen] = useState(false);
  const [itemIdBeingEdited, setItemIdBeingEdited] = useState(initialEditId());
  // Which record's saved values are currently seeded into the form. The fetch effect sets it once the
  // data arrives (or on failure). editDataLoading (derived below, during render) compares it against
  // the record being edited to decide whether to show a spinner — deriving it in render rather than a
  // post-paint effect means the empty/default (invalid) values never flash, even on the in-place
  // list -> edit transition where the component is not remounted.
  const [seededEditId, setSeededEditId] = useState(null);
  const [postOnChangeResponse, setPostOnChangeResponse] = useState({});
  const [values, setValues] = useState(initialValues());
  const [inCompleteMessage, setInCompleteMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const outputCurrencies = useRef([]);
  const {register, handleSubmit, clearErrors, trigger, formState: { errors: allErrors }} = useForm();

  // Track which fields the user has interacted with, and whether a save has been attempted, so
  // that validation errors are only DISPLAYED on fields the user has reached. Validation itself
  // still runs over all fields (save is blocked by trigger()); this only gates what is shown.
  const [touchedFields, setTouchedFields] = useState({});
  const [saveAttempted, setSaveAttempted] = useState(false);

  // Track if this is the initial render to avoid URL update on mount
  const isInitialRender = useRef(true);

  // Update URL when editing state changes (skip initial render)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    updateUrlForEditState(componentShowingState, itemIdBeingEdited, config.key);
  }, [componentShowingState, itemIdBeingEdited, config.key]);

  const scrollTop = () => {
    if(window.QuoteBuilderFormScrollTop) {
      $("html, body").animate({ scrollTop: 100 }, "slow");
    }
  }

  const setValue = (id, value) => {
    setValues( (previous) => {
      if (typeof value === 'function') {
        previous[id] = value(previous);
      } else {
        previous[id] = value;
      }
      return { ...previous };
    })
  }

  // Record a field as touched. Driven by the form-level onBlur, which bubbles every child input's
  // blur (event.target.name is the blurred field). Keeps premature errors off untouched fields,
  // e.g. picking a colour count must not flag the still-untouched price field further down.
  const markFieldTouched = (name) => {
    if (!name) { return; }
    setTouchedFields((previous) => previous[name] ? previous : { ...previous, [name]: true });
  };

  // Only the errors the user should currently see: a field's error shows once that field is
  // touched, or once a save has been attempted (which reveals everything at once). Every
  // errors={errors} prop below consumes this filtered set, so the gating applies form-wide.
  const errors = {};
  for (const name in allErrors) {
    if (saveAttempted || touchedFields[name]) {
      errors[name] = allErrors[name];
    }
  }

  const getDefaultFetchPath = () => {
    if(editedKlass == "UserUnitPriceAttribute") {
      return `/quote-builder/user_unit_price_attributes/properties/${itemIdBeingEdited}`
    }

    return (config.fetch_path || default_fetch_path).replace(':id', itemIdBeingEdited);
  }

  const getDefaultUpdatePath = () => {
    if(editedKlass == "UserUnitPriceAttribute") {
      return `/quote-builder/user_unit_price_attributes/update_properties/${itemIdBeingEdited}`
    }

    return config.submit.update_path || default_update_path;
  }

  const getFetchEnabled = () => {
    if(editedKlass == "UserUnitPriceAttribute") { return true }
    return config.fetch_enabled;
  }

  useEffect(() => {
    if(disabled) { return };
    if(getFetchEnabled() && itemIdBeingEdited > 0 && componentShowingState == STATE_EDIT) {
      const idBeingFetched = itemIdBeingEdited;
      setFetchInProgress(true);

      GetRequest({
        url: getDefaultFetchPath(),
        callback: (data) => {
          data.price_attributes = initialValues()['price_attributes'];
          setFetchInProgress(true);

          for (const [key, value] of Object.entries(data)) {
            // A price that arrives null (a machine-only record saved before pricing existed)
            // must not lock the input in its "Loading..." state: seed an editable zero instead.
            if (value === null && data.price_attributes.includes(key)) {
              setValue(key, zeroPriceValueFor(key));
            } else if (value === null && multiSelectDefaultFor(key) != undefined) {
              setValue(key, multiSelectDefaultFor(key));
            } else {
              setValue(key, value);
            }
          }

          seedSaveValueIntoFields(config.attributes, data, setValue, makeVap);

          if(data.is_system != undefined && setIsSystem != undefined) {
            setIsSystem(data.is_system);
          }

          scrollTop();

          setFetchInProgress(false);
          setSeededEditId(idBeingFetched);
        },
        // On any failure (403/404/500/network) mark this record resolved so the form shows (with
        // unseeded values) rather than spinning forever.
        onError: () => {
          setFetchInProgress(false);
          setSeededEditId(idBeingFetched);
        }
      })
    }

    if(config.fetch_enabled && itemIdBeingEdited > 0 && componentShowingState == STATE_COPY_FROM_SYSTEM) {
      setFetchInProgress(true);

      GetRequest({
        url: default_fetch_system_path.replace(':id', itemIdBeingEdited),
        callback: (data) => {

          data.price_attributes = initialValues()['price_attributes'];

          setFetchInProgress(true);

          for (const [key, value] of Object.entries(data)) {
            if(config.mark_system.system_attribute_names.includes(key)) {
              setValue(key, value);
            }
          }

          setValue('copied_from_id', itemIdBeingEdited);

          scrollTop();
          setFetchInProgress(false);

          setTimeout(() => {
            setComponentShowingState(() => { return STATE_LIST});
            setTimeout(() => {setComponentShowingState(() => { return STATE_COPY_FROM_SYSTEM});}, 2)
          }, 2)
        }
      })
    }

  }, [ itemIdBeingEdited ]);

  useEffect( () => {
    if(disabled) { return };

    if(componentShowingState == STATE_NEW) {
      if(window.QuoteBuilderFormScrollTop) { scrollTop() }
      setValues( { ...initialValues() } );
    }
  }, [componentShowingState])

  // Start each form context (new record, editing a different record, switching list/edit) with a
  // clean slate so previously touched fields / a prior save attempt don't carry over and reveal
  // errors on a freshly opened form.
  useEffect(() => {
    setTouchedFields({});
    setSaveAttempted(false);
  }, [componentShowingState, itemIdBeingEdited])

  const save = async () => {
    if(disabled) { return };
    setSubmitInProgress( () => true )

    setFetchInProgress(true);

    if(beforeSave != undefined) beforeSave();

    let url;

    switch(componentShowingState) {
      case STATE_NEW:
        url = (config.submit.create_path || default_create_path).replace(':key', config.key)
        break;

      case STATE_COPY_FROM_SYSTEM:
        url = (config.submit.create_path || default_create_path).replace(':key', config.key)
        break;

      case STATE_EDIT:
        url = getDefaultUpdatePath()
        break;

      default:
        throw 'invalid componentShowingState';
    }

    if(await trigger()) {

      applySaveValueInto(config.attributes, values, makeVap);

      if(has_file.current != undefined && has_file.current != '') {
        values.file_fields = [ has_file.current ] ;
        values[ has_file.current ] = $(`[name='${has_file.current }']`)[0].files[0]
      }

      PostRequest({
        url: url,
        data: values,
        callback: (data) => {
          setItemIdBeingEdited(null);
          setFetchInProgress(false);
          document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_saved', {}) );
          if(afterSave != undefined) {
            afterSave(data.id);
          } else {
            setComponentShowingState(STATE_LIST);
          }

         setSubmitInProgress( () => false )
        },
        400: (data) => {
          setErrorMessage(data.errors);
          setItemIdBeingEdited(null);
          setFetchInProgress(false);
          setSubmitInProgress( () => false );
        }
      }).catch((error) => {
        console.error('[QuoteBuilderForm save] Unhandled error:', error);
        setFetchInProgress(false);
        setSubmitInProgress(() => false);
        setItemIdBeingEdited(null);
      })
    } else {
      setSubmitInProgress( () => false );
    }
  }

  const cancel = () => {
    clearErrors();
    setItemIdBeingEdited(null);
    setComponentShowingState(STATE_LIST);
    if(afterCancel != undefined) {
      afterCancel();
    }
  }

  const windowValue = () => {
    let key = 'window_type__default';

    // item is a quote-flow nav-column concept; on the settings page (a UserUnitPrice picker used
    // outside a quote, e.g. the cellophane machine's material link) there is no item, so fall back
    // to the default window key instead of dereferencing undefined.
    if(item != undefined && item.layout_part_group_code != '' && item.layout_part_group_code != undefined && item.layout_part_group_code != null) {
      key = `window_type_${item.layout_part_group_code}`;
    }

    return values[key];
  }

  // requestGetExtraItems
  const requestGetExtraItemsRef = useRef([]);
  const requestGetExtraItems = (key_value) => {
    requestGetExtraItemsRef.current.push([key_value, section_uuid]);
  }

  const processGetExtraItemRequests = () => {
    for(let i = 0; i < requestGetExtraItemsRef.current.length; i++) {
      let args = requestGetExtraItemsRef.current[i];
      getExtraItems(args[0], args[1], values, item, options);
    }

    requestGetExtraItemsRef.current = [];
    if(config.get_extra_items_on_change) {
      getExtraItems({ key: config.get_extra_items_key, value: config.get_extra_items_value }, eval(config.get_extra_items_uuid_eval) , values, item, options);
    }
  }

  //requestPublishItemOnChange
  const requestPublishItemOnChangeRef = useRef([]);
  const requestPublishItemOnChange = (msg) => {
    requestPublishItemOnChangeRef.current.push(msg);
  }

  const processPublishItemOnChange = () => {
    for(let i=0; i < requestPublishItemOnChangeRef.current.length; i++) {
      let message = requestPublishItemOnChangeRef.current[i];
      let publishItemKey = message.publish_item_key != undefined ? message.publish_item_key : USER_UNIT_PRICE_CHANGED_WITH_ITEM_EVENT_KEY;

      message.values = values;
      message.item = item;
      message.nav_section = navSection;

      // console.log({key: publishItemKey, message: message})
      document.dispatchEvent(new CustomEvent(publishItemKey, { detail: message } ));
    }
    requestPublishItemOnChangeRef.current = [];
  }

  const formFieldFactory = (config, index) => {
    let formField;

    switch(config.form_type) {
      case 'UserUnitPriceAttribute':
        formField = <UserUnitPriceAttributeField
            key={keyFor(config)}
            config={config}
            options={options}
            setValue={setValue}
            readOnlyValues={readOnlyValues}
            setFetchInProgress={setFetchInProgress}
            setChildSubFormOpen={setChildSubFormOpen}
            value={values[config.name]}
            values={values}
            disabled={disabled}
            register={register}
            errors={errors}
            context={context}
            item={item}
            navSection={navSection}
            requestPublishItemOnChange={requestPublishItemOnChange}
            onAttributesLoaded={registerAttributeProperties}
          />
        break;

      case 'UserUnitPrice':
        addToUsetUnitPriceAttributes(config.name);

        formField = <UserUnitPrice
            key={keyFor(config)}
            config={config}
            value={values[config.name]}
            values={values}
            setValue={setValue}
            windowValue={windowValue()}
            options={options}
            item={item}
            navSection={navSection}
            setFetchInProgress={setFetchInProgress}
            disabled={disabled}
            requestGetExtraItems={requestGetExtraItems}
            requestPublishItemOnChange={requestPublishItemOnChange}
            navSectionColumnType={navSectionColumnType}
          />
        break;

      case 'select':
        formField = <SelectFormField
            key={keyFor(config)}
            config={config}
            options={options}
            setValue={setValue}
            item={item}
            navSection={navSection}
            readOnlyValues={readOnlyValues}
            setFetchInProgress={setFetchInProgress}
            requestGetExtraItems={requestGetExtraItems}
            value={values[config.name]}
            values={values}
            requestPublishItemOnChange={requestPublishItemOnChange}
            d={d}
            disabled={disabled}
            register={register}
            errors={errors}
            context={context}
          />
        break;

      case 'layout_dimension_select':
        formField = <LayoutDimensionSelectFormField
            key={keyFor(config)}
            config={config}
            value={values[config.name]}
            values={values}
            setValue={setValue}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'heading':
        formField = <HeadingFormField
            key={keyFor(config)}
            config={config}
          />
        break;

      case 'glueing_max_material_length_select':
        formField = <GlueingMaxMaterialLengthSelectFormField
            key={keyFor(config)}
            config={config}
            value={values[config.name]}
            setValue={setValue}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'glueing_point_count_select':
        formField = <GlueingPointCountSelectFormField
            key={keyFor(config)}
            config={config}
            value={values[config.name]}
            values={values}
            setValue={setValue}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'hidden':
        formField = <HiddenFormField
            key={keyFor(config)}
            config={config}
            options={options}
            setValue={setValue}
            item={item}
            navSection={navSection}
            setFetchInProgress={setFetchInProgress}
            requestGetExtraItems={requestGetExtraItems}
            value={values[config.name]}
            disabled={disabled}
          />
        break;

      case 'length':
        formField = <LengthFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            setFetchInProgress={setFetchInProgress}
            value={values[config.name]}
            register={register}
            errors={errors}
            unit= {config.length_unit != undefined ? config.length_unit : values[config.length_unit_in_values]}
            disabled={disabled}
            readOnlyValues={readOnlyValues}
            context={context}
          />
        break;

      case 'number':
        formField = <NumberFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            setFetchInProgress={setFetchInProgress}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'delayed_number':
        formField = <DelayedNumberFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            item={item}
            navSection={navSection}
            setFetchInProgress={setFetchInProgress}
            requestPublishItemOnChange={requestPublishItemOnChange}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'integer':
        formField = <NumberFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            integerOnly={true}
            setFetchInProgress={setFetchInProgress}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'delayed_integer':
        formField = <DelayedNumberFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            item={item}
            navSection={navSection}
            integerOnly={true}
            setFetchInProgress={setFetchInProgress}
            requestPublishItemOnChange={requestPublishItemOnChange}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'delayed_integer_with_unit':
        formField = <DelayedNumberWithUnitFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            item={item}
            navSection={navSection}
            integerOnly={true}
            setFetchInProgress={setFetchInProgress}
            unit= {config.length_unit != undefined ? config.length_unit : values[config.length_unit_in_values]}
            requestPublishItemOnChange={requestPublishItemOnChange}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'delayed_integer_with_static_units':
        formField = <DelayedNumberWithStaticUnitsFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            integerOnly={true}
            requestPublishItemOnChange={requestPublishItemOnChange}
            value={values[config.name]}
            values={values}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'string':
        formField = <StringFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            setFetchInProgress={setFetchInProgress}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'file':
        has_file.current = config.name;

        formField = <FileFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            setFetchInProgress={setFetchInProgress}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'email':
        formField = <StringFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            setFetchInProgress={setFetchInProgress}
            value={values[config.name]}
            register={register}
            errors={errors}
            email={true}
            disabled={disabled}
          />
        break;

      case 'textArea':
        formField = <TextAreaFormField
            key={keyFor(config)}
            config={config}
            setValue={setValue}
            setFetchInProgress={setFetchInProgress}
            value={values[config.name]}
            register={register}
            errors={errors}
            disabled={disabled}
          />
        break;

      case 'currency':
        formField = <CurrencyFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          disabled={disabled}
        />
        break;

      case 'CurrencyExchange':
        formField = <CurrencyExchange
          key={config.name}
          config={config}
        />
        break;

      case 'LayoutPartGroupsWindows':
        formField = <LayoutPartGroupsWindows
          key={config.name}
          config={config}
          values={values}
          setValue={setValue}
        />
        break;

      case 'LayoutPartGroupsWindowsHidden':
        formField = <LayoutPartGroupsWindowsHidden
          key={config.name}
          config={config}
          values={values}
          setValue={setValue}
        />
        break;

      case 'price':
        formField = <PriceFormField
          key={config.name}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          currency={values['currency']}
          register={register}
          errors={errors}
          value={values[config.name]}
          values={values}
          disabled={disabled}
          precision={config.precision}
          dataSourcesEnabled={window.quote_builder_data_sources_enabled}
          userUnitPriceId={itemIdBeingEdited}
        />
        break;

      case 'price_with_precision_4':
        formField = <PriceFormField
          key={config.name}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          currency={values['currency']}
          register={register}
          errors={errors}
          value={values[config.name]}
          values={values}
          disabled={disabled}
          precision="4"
          dataSourcesEnabled={window.quote_builder_data_sources_enabled}
          userUnitPriceId={itemIdBeingEdited}
        />
        break;

        case 'description_long':
        formField = showLongDescriptions && <DescriptionLong
          key={config.name}
          values={values}
          config={config}
          disabled={disabled}
        />
        break;

        case 'checkboxes':
        formField = <CheckboxesFormField
            key={keyFor(config)}
            config={config}
            options={options}
            setValue={setValue}
            item={item}
            navSection={navSection}
            readOnlyValues={readOnlyValues}
            setFetchInProgress={setFetchInProgress}
            requestGetExtraItems={requestGetExtraItems}
            value={values[config.name]}
            values={values}
            requestPublishItemOnChange={requestPublishItemOnChange}
            d={d}
            disabled={disabled}
            register={register}
            errors={errors}
            context={context}
          />
        break;

        case 'boolean':
        formField = <BooleanFormField
            key={keyFor(config)}
            config={config}
            options={options}
            setValue={setValue}
            item={item}
            navSection={navSection}
            readOnlyValues={readOnlyValues}
            setFetchInProgress={setFetchInProgress}
            requestGetExtraItems={requestGetExtraItems}
            value={values[config.name]}
            values={values}
            requestPublishItemOnChange={requestPublishItemOnChange}
            d={d}
            disabled={disabled}
            register={register}
            errors={errors}
            context={context}
          />
        break;

        case 'drawing_filtering':
        formField = <DrawingFiltering
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          register={register}
          errors={errors}
          disabled={disabled}
        />
        break;
      }

      if(config.visible_if != undefined) {
        return eval(config.visible_if) && formField
      }

      return formField;
   }

   const myFormRef = useRef();

   const submit = async () => {
    // Reveal every field's error on a save attempt (this is the real Save path: the button is
    // type='button' with onClick={submit}, so an invalid save would otherwise show nothing).
    setSaveAttempted(true);
    if(await trigger()) {
      myFormRef.current.handleSubmit(save);
    }
  };

  useEffect(() => {
    const keyDownHandler = event => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  const onPostOnChangeCompletedEnabled = useRef(false);

  const onPostOnChangeCompleted = (data) => {
    document.dispatchEvent(new CustomEvent('quote_builder.section_result_updated', { detail: section_uuid }));

    if(data.user_quote_section_properties != undefined) {
      window.user_quote_section_properties = data.user_quote_section_properties;
    }

    if(data.user_quote != undefined) {
      window.user_quote = data.user_quote;
    }

    if(data.nav_sections != undefined) {
      window.nav_sections = data.nav_sections;
    }
  }

  const anyUserUnitPriceAttributeIsSet = useRef(false);
  const valuesHash = useRef();

  const postOnChange = async () => {
    if(disabled) { return };
    if(valuesHash.current != undefined && hash(values) == valuesHash.current) { return }

    setInCompleteMessage(null);
    setErrorMessage(null);
    setPostOnChangeResponse([]);

    if(anyUserUnitPriceAttributeIsSet.current == false && userUnitPriceAttributes.current != undefined) {
      for(let i = 0; i < userUnitPriceAttributes.current.length; i++) {
        if(values[userUnitPriceAttributes.current[i]] != VALUE_NOT_AVAILABLE) {
          anyUserUnitPriceAttributeIsSet.current = true;
        }
      }
    }
    if(anyUserUnitPriceAttributeIsSet.current == false && userUnitPriceAttributes.current != undefined && userUnitPriceAttributes.current.length > 0) {
      setTimeout(processPublishItemOnChange, 250);
      return;
    }

    if(await trigger()) {
      let valuesToPost = { ...values };
      // if(sectionClearEnabled.current) { valuesToPost.section_clear_enabled = true; }
      valuesHash.current = hash(values);

      // if(hasFile) { valuesToPost.has_file = hasFile }
      if(has_file.current != undefined && has_file.current != '') {
        // todo
      }

      PostRequest({
        url: config.post_on_change_path,
        data: valuesToPost,
        callback: (data) => {
          if(data.results != undefined) {
            setPostOnChangeResponse(data.results);
            outputCurrencies.current = data.results.currencies;
          }
          onPostOnChangeCompleted(data);
          onPostOnChangeCompletedEnabled.current = true;
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(false);
        },
        422: (data) => {
          setInCompleteMessage(data.message);
          setPostOnChangeResponse({line_item_prices: []});
          if(onPostOnChangeCompletedEnabled.current) { onPostOnChangeCompleted(data); }
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(false);
        },
        400: (data) => {
          setErrorMessage(data.message);
          setPostOnChangeResponse({line_item_prices: []});
          if(onPostOnChangeCompletedEnabled.current) { onPostOnChangeCompleted(data); }
          processPublishItemOnChange();
          processGetExtraItemRequests();
          setFetchInProgress(false);
        }
      });
    }
  }

  const validateOnChange = () => {
    if(disabled) { return };
    setInCompleteMessage(null);
    setErrorMessage(null);

    trigger();
  }

  useEffect(() => {
   if(disabled) { return };

   if(config.post_on_change && values['updated_at'] != undefined){
    postOnChange();
   }
  }, [values['updated_at']]);

  const disabledIf = () => {
    if(submitInProgress) { return true }
    if(childSubFormOpen) { return true }
    return (config.submit.disabled_if != undefined && eval(config.submit.disabled_if))
  }

  useEffect(() => {
    let event = new CustomEvent('quote_builder.output_currencies_changed', { "detail": outputCurrencies.current });
    document.dispatchEvent(event);

  }, [outputCurrencies.current]);

  useEffect(() => {
    if(config.post_on_change) {
      let listener = function(e) {
        setValue('user_exchange_rates', (previous) => {
          previous['user_exchange_rates'][e.detail.key] = e.detail.value;
          return { ...previous['user_exchange_rates'] };
        });

        setValue('updated_at', Date.now());
      };

      document.addEventListener('quote_builder.currency_exchange_rate_changed', listener);
      return () => { document.removeEventListener('quote_builder.currency_exchange_rate_changed', listener);}
    }
  }, []);

  const sectionClearEnabled = useRef(false);

  const onKeyUpWrapped = (e) => {
    // sectionClearEnabled.current = true;
    postOnChange()
  }

  const onBlurWrapped = (e) => {
    // sectionClearEnabled.current = true;
    postOnChange()
  }
  const cookieNameforLongDescription = 'quote_builder_settings_show_long_descriptions';
  const [showLongDescriptions, setShowLongDescriptions] = useState(true);

  const toggleDescriptionsControl = () => {
    return (
      <label className="switch-small" title={t('quote_builder.toggle_summaries')}>
        <input type="checkbox" className="pref" checked={showLongDescriptions} onChange={handleToggleLongDescriptions}/>
        <span className="slider-small"></span>
      </label>);
  }
  const handleToggleLongDescriptions = () => {
    setShowLongDescriptions(!showLongDescriptions);
    setCookie(cookieNameforLongDescription, !showLongDescriptions, 365)
  }

  const titleTr = () => {
    return (config.new_title != undefined || config.edit_title != undefined || config.title != undefined) && (
      <tr>
        <th colSpan="2">
          {config.new_title != undefined && componentShowingState == STATE_NEW && t(config.new_title) }
          {config.new_title != undefined && componentShowingState == STATE_COPY_FROM_SYSTEM && t(config.new_title) }
          {config.edit_title != undefined && componentShowingState == STATE_EDIT && t(config.edit_title)}
          {config.title != undefined && t(config.title) } {false && config.title != undefined && extra_title != undefined && ` (${t(extra_title)})` }
          {config.toggle_long_descriptions && <div className="float-right"> {toggleDescriptionsControl()} </div>}

          {localDisabledReason && (<span className="float-right"> <small className="q-read-only-message"> {localDisabledReason} <br /> </small> </span>)}
        </th>
      </tr>);
  }

  const [localDisabled, setLocalDisabled] = useState(false);
  const [localDisabledReason, setLocalDisabledReason] = useState('');
  if(config.disabled_if != undefined) {
    useEffect(() => {
      if( eval(config.disabled_if) ) {
        setLocalDisabled( (_prev) => true )
        setLocalDisabledReason( (_prev) => t(config.disabled_if_reason) );
      } else {
        setLocalDisabled( (_prev) => false )
        setLocalDisabledReason( (_prev) => null);
      }
    }, [values])
  }

  // Section-level hidden_if: unlike disabled_if (greyed out with a reason), a hidden section is
  // removed from view entirely while staying mounted, so its hidden fields keep listening and it
  // keeps posting on changes (letting its calculator clear its saved total server-side).
  const [localHidden, setLocalHidden] = useState(false);
  if(config.hidden_if != undefined) {
    useEffect(() => {
      if( eval(config.hidden_if) ) {
        setLocalHidden( (_prev) => true )
      } else {
        setLocalHidden( (_prev) => false )
      }
    }, [values])
  }

  const [isSystem, setIsSystem] = useState(false);
  const handleIsSystemChange = (e) => {
    PostRequest({
      url: `/quote-builder/user_unit_prices/update-is-system`,
      data: {
        id: itemIdBeingEdited,
        is_system: e.target.checked
      },
      callback: (data) => {
        setIsSystem(() => {return data.is_system})
      }
    })
  }

  const isMarkSystemEnabled = () => {
    return componentShowingState == STATE_EDIT && config.mark_system != undefined && config.mark_system.enabled && window.mark_system_enabled == 'true';
  }

  const getSaveInnerHtml = () => {
    if(saveLabel != undefined && saveLabel != '') { return saveLabel}
    if(submitInProgress) { return t('three_d.please_wait') }
    if(!submitInProgress && componentShowingState == STATE_NEW) { return t('quote_builder.save') }
    if(!submitInProgress && componentShowingState == STATE_COPY_FROM_SYSTEM) { return t('quote_builder.save') }
    if(!submitInProgress && componentShowingState == STATE_EDIT ) { return t('quote_builder.update') }
  }

  // Show the spinner (instead of the form) whenever we are editing a record whose saved values have
  // not been seeded yet. Only when a fetch will actually run (edit state, not disabled, fetch-enabled,
  // real id) — otherwise there is nothing to wait for and the form renders straight away. Derived in
  // render so the empty-form frame never paints, and gated on STATE_EDIT so cancelling mid-fetch does
  // not leave a spinner over the list.
  const editDataLoading =
    componentShowingState == STATE_EDIT &&
    !disabled &&
    getFetchEnabled() &&
    itemIdBeingEdited > 0 &&
    String(seededEditId) !== String(itemIdBeingEdited);

  return (
    <div className={`${config.class_name != undefined ? config.class_name : ''} ${(fetchInProgress || disabled || localDisabled) ? 'disabledForm' : ''}`} style={localHidden ? { display: 'none' } : undefined} data-section-uuid={section_uuid} id={section_uuid} >
      {config.list.enabled && componentShowingState == STATE_LIST && <QuoteBuilderList config={config} setComponentShowingState={setComponentShowingState} setItemIdBeingEdited={setItemIdBeingEdited} hideDeleteFromList={hideDeleteFromList}/> }

      {editDataLoading && (
        <div className="quote-builder-form-loading" style={{ padding: '40px 20px', textAlign: 'center', color: '#888' }}>
          <i className="fa fa-spinner fa-spin fa-2x"></i>
          <div style={{ marginTop: '12px' }}>{t('quote_builder.please_wait')}</div>
        </div>
      )}

      {!editDataLoading && (componentShowingState == STATE_NEW || componentShowingState == STATE_EDIT || componentShowingState == STATE_COPY_FROM_SYSTEM) && (
        <Form onSubmit={handleSubmit(save, () => setSaveAttempted(true))} ref={myFormRef} onBlur={(event) => { markFieldTouched(event.target.name); (config.post_on_change ? onBlurWrapped : validateOnChange)(event); }}>
         <table className="table table-striped table-bordered-outside quote_builder_table">
          <tbody>
            {titleTr()}

            {config.attributes.map(formFieldFactory)}

            {(config.submit.enabled || config.cancel.enabled) &&
              <tr key="one">
                <td colSpan="2">
                  {config.cancel.enabled && <button className="btn" disabled={childSubFormOpen} onClick={cancel}> {t('quote_builder.cancel')} </button>}

                  &nbsp;
                  &nbsp;

                  {config.submit.enabled && <button className="btn simple-btn" type='button' disabled={disabledIf()} onClick={submit} >
                    {getSaveInnerHtml()}
                  </button> }

                  {linkToSettings && <div className="float-right"> <small> <a href={`/quote-builder/settings/${config.key}`} target="_blank"> <i className="fa fa-cog" aria-hidden="true"></i> {t('nav.quote_builder_settings_long')} </a>  </small> </div> }
                </td>
              </tr>}

              {((config.post_on_change && postOnChangeResponse != undefined && postOnChangeResponse.line_item_prices != undefined && postOnChangeResponse.line_item_prices.length > 0) || inCompleteMessage || errorMessage) && (
                 <tr key="two">
                  <td></td>
                  <td colSpan="1">
                     {config.post_on_change && postOnChangeResponse.line_item_prices.length > 0 && (
                        <table className="float-right full_width">
                          <tbody>

                          {config.show_number_of_sheets != false && postOnChangeResponse.number_of_sheets != undefined && (
                          <tr key="number_of_sheets">
                            <td>
                              {t('quote_builder.number_of_sheets')}
                              {postOnChangeResponse.num_of_drawings_in_set > 1 && (
                                <Tooltip title={t('quote_builder.num_of_drawings_in_set_description').replace('%s', postOnChangeResponse.num_of_drawings_in_set)} />)}
                            </td>
                            <td className="align-right"> {postOnChangeResponse.number_of_sheets}</td>
                          </tr>
                          ) }

                          { postOnChangeResponse.result_info_rows.map( (resultInfoRow) => {
                            return (<tr key={resultInfoRow.key}>
                              <td>
                                {t(resultInfoRow.key)}
                                {resultInfoRow.tooltip_text != undefined && (
                                  <Tooltip title={resultInfoRow.tooltip_text} />)}
                              </td>
                              <td className="align-right">
                                {resultInfoRow.value != undefined && resultInfoRow.value}
                              </td>
                            </tr>)
                          } )}

                          {postOnChangeResponse.line_item_prices.map((line_item_price) => {
                            return (
                              <tr key={line_item_price.title}>
                                <td> {line_item_price.title} {line_item_price.notes != '' && <Tooltip key={line_item_price.notes} title={line_item_price.notes} />} </td>
                                <td className="align-right">
                                {line_item_price.price_formatted}

                                 {line_item_price.price_in_pence > 0 && line_item_price.exchange_info_presented != '' && (
                                    <Tooltip key={line_item_price.exchange_info_presented} title={line_item_price.exchange_info_presented} />
                                  )}
                                </td>
                              </tr>
                            )
                          })}

                          {config.show_price_for_one && (<>
                            <tr className="tr-bordered-top">
                              <td> {t('quote_builder.price_for_one')} </td>
                              <td className="align-right"> {postOnChangeResponse.price_for_one_formatted}</td>
                            </tr>

                           {config.show_price_for_sheet && (
                            <tr className="">
                              <td> {t('quote_builder.price_for_sheet')} </td>
                              <td className="align-right"> {postOnChangeResponse.price_for_sheet_formatted}</td>
                            </tr>

                            )}
                            <tr className="">
                              <td> {t('quote_builder.total')} </td>
                              <td className="align-right"> {postOnChangeResponse.total_price_formatted}</td>
                            </tr>
                          </>)}

                          {config.show_price_for_one != true && (<>
                             <tr className="tr-bordered-top">
                              <td> {t('quote_builder.total')} </td>
                              <td className="align-right"> {postOnChangeResponse.total_price_formatted}</td>
                            </tr>
                          </>)}
                          </tbody>
                        </table>
                      )}

                  </td>
                </tr>
              )}

            {(isMarkSystemEnabled() && (
              <tr key="isMarkSystemEnabled">
                <td colSpan="2">
                  <div className="float-right">
                    <input type="checkbox" className="pref" checked={isSystem} onChange={handleIsSystemChange} /> Kütüphaneye dahil
                    </div>
                </td>
              </tr>
            ))}

          </tbody>
        </table>

        {inCompleteMessage && (<>
        <div className="alert alert-danger fade in">
          <i className="fa fa-exclamation-circle"></i>
            &nbsp;
            <strong>
              <span dangerouslySetInnerHTML={{__html: inCompleteMessage}}></span>
            </strong>
          </div>

        </>)}

        {errorMessage && (<>
          <div className="alert alert-danger fade in">
             <i className="fa fa-exclamation-circle"></i>
             &nbsp;
            <strong>
              <span dangerouslySetInnerHTML={{__html: errorMessage}}></span>
            </strong>
          </div>

        </>)}

      </Form>
      )}

    </div>
  )
}

export default QuoteBuilderForm;
